/* Cart Rewards Bar — v3
   Changes (v3):
   - Adds support for excluding products from free-shipping subtotal via
     data-exclude-product-ids="123,456" on #cart-rewards-bar
   - Excluded products are ignored in the subtotal used for progress/state
   - Keeps existing logic: bundle badge detection, events, mutation observer, etc.
*/

(function () {
  "use strict";

  const BAR_ID = "cart-rewards-bar";
  const PROGRESS_ID = "rewards-progress";

  const $ = (s) => document.querySelector(s);
  const $$ = (s) => Array.from(document.querySelectorAll(s));
  const $bar = () => document.getElementById(BAR_ID);
  const $progress = () => document.getElementById(PROGRESS_ID);

  const fetchCart = () =>
    fetch("/cart.js", { credentials: "same-origin" })
      .then((r) => r.json())
      .catch(() => null);

  // --- Debug helper
  function log(...args) {
    console.log("[Rewards DEBUG]", ...args);
  }

  // ✅ Parse excluded product ids from dataset: "123,456"
  function getExcludedProductSet(barEl) {
    const raw = (barEl?.dataset?.excludeProductIds || "").trim();
    if (!raw) return new Set();
    return new Set(
      raw
        .split(",")
        .map((x) => Number(String(x).trim()))
        .filter((n) => Number.isFinite(n) && n > 0)
    );
  }

  // More robust "final line price" resolver
  function getLineFinal(it) {
    if (typeof it?.final_line_price === "number") return it.final_line_price;

    const lp = Number(it?.line_price || 0);
    const td = Number(it?.total_discount || 0);
    return Math.max(0, lp - td);
  }

  // Subtotal for progress calculation:
  // - include only items that require shipping
  // - exclude items whose product_id is in excludeSet
  // - subtract cart-level discount applications only if line allocations not present
  function computeSubtotal(cart, excludeSet) {
    let subtotal = 0;

    for (const it of cart?.items || []) {
      if (!it?.requires_shipping) continue;

      const pid = Number(it?.product_id || 0);
      if (excludeSet && excludeSet.has(pid)) continue;

      subtotal += getLineFinal(it);
    }

    // In most Shopify carts, final_line_price already includes per-line allocations.
    // Cart-level discount allocations can be represented in different ways by themes/apps.
    // We only subtract cart_level_discount_applications if there are NO line allocations at all.
    const hasAllocations = (cart?.items || []).some(
      (it) => Array.isArray(it?.discount_allocations) && it.discount_allocations.length > 0
    );

    if (!hasAllocations) {
      for (const da of cart?.cart_level_discount_applications || []) {
        subtotal -= Number(da?.total_allocated_amount || 0);
      }
    }

    return Math.max(0, subtotal);
  }

  function setPct(p) {
    const el = $progress();
    if (el) el.style.width = `${Math.min(100, Math.max(0, p))}%`;
  }

  function paintSteps(done) {
    $$(`#${BAR_ID} .step`).forEach((s) =>
      s.classList.toggle("completed", done.has(+s.dataset.stepIndex))
    );
  }

  // ✅ Detect dynamic Bundle discount badge
  function isBundleDiscountActive() {
    const badge = document.querySelector(".discount-badge");
    if (!badge) return false;
    return badge.textContent.toLowerCase().includes("bundle");
  }

  // ---------- amount formatter ----------
  function formatAmount(cents) {
    const val = cents / 100;
    return `$${val % 1 === 0 ? val.toFixed(0) : val.toFixed(2).replace(/0$/, "")}`;
  }

  // ---------- helper to inject [amount] ----------
  function updateAmountPlaceholder(container, amount) {
    if (!container) return;
    if (!container.dataset.template) container.dataset.template = container.innerHTML;
    const tpl = container.dataset.template;
    container.innerHTML = tpl.replace(/\[amount\]/gi, `<span class="amount">${amount}</span>`);
  }

  function updateMessage(state, leftAmount = 0) {
    const step1 = $("#msg-step-1");
    const step2 = $("#msg-step-2");
    const step3 = $("#msg-step-3");

    [step1, step2, step3].forEach((el) => el?.classList.add("hidden"));

    const formatted = formatAmount(leftAmount * 100);

    if (state === 1) {
      step1?.classList.remove("hidden");
      updateAmountPlaceholder(step1, formatted);
    } else if (state === 2) {
      step2?.classList.remove("hidden");
    } else if (state === 3) {
      step3?.classList.remove("hidden");
    }
  }

  async function refresh() {
    const el = $bar();
    if (!el) return;

    const threshold = Number(el.dataset.freeShippingThreshold || 0);
    const excludeSet = getExcludedProductSet(el);

    const cart = await fetchCart();
    if (!cart) return;

    const subtotal = computeSubtotal(cart, excludeSet);
    const bundleActive = isBundleDiscountActive();

    let state = 1;
    let progressPct = 0;
    let left = 0;

    // progress bar uses first segment for free shipping
    const shippingSegment = 33.33;

    if (bundleActive) {
      progressPct = 100;
      state = 3;
    } else if (subtotal >= threshold) {
      progressPct = shippingSegment;
      state = 2;
    } else {
      progressPct = threshold > 0 ? (subtotal / threshold) * shippingSegment : 0;
      left = (threshold - subtotal) / 100; // dollars left
      state = 1;
    }

    paintSteps(
      new Set([
        ...(subtotal >= threshold || bundleActive ? [1] : []),
        ...(bundleActive ? [2] : []),
      ])
    );

    setPct(progressPct);
    updateMessage(state, left);
  }

  function wire() {
    refresh();

    ["cart:change", "cart:updated", "cart:refresh", "cart-drawer:refreshed"].forEach((evt) =>
      document.addEventListener(evt, refresh, { passive: true })
    );

    document.addEventListener("change", (e) => {
      if (e.target?.matches?.(".quantity-selector__input")) setTimeout(refresh, 200);
    });

    document.addEventListener("click", (e) => {
      if (e.target?.closest?.(".quantity-selector__button")) setTimeout(refresh, 300);
    });

    // ✅ Watch for dynamic cart updates
    const drawer = document.querySelector("cart-drawer, #CartDrawer, .drawer");
    if (drawer) {
      const observer = new MutationObserver(() => {
        clearTimeout(window.__rewardsDelay);
        window.__rewardsDelay = setTimeout(refresh, 300);
      });
      observer.observe(drawer, { childList: true, subtree: true });
    }
  }

  document.addEventListener("DOMContentLoaded", wire);
})();